"use client";

import { useTheme } from "@/contexts/ThemeContext";

/**
 * Dark / Light control.
 *
 * Two shapes for two contexts: `segmented` for settings-style surfaces where
 * both options should be visible and labelled, and `icon` for HUD rails where
 * space is the constraint.
 *
 * Both render the same state and write through the same provider, so the
 * atlas footer, the /app toolbar and account settings can never disagree.
 */
export default function ThemeToggle({
  variant = "segmented",
  className = "",
}: {
  variant?: "segmented" | "icon";
  className?: string;
}) {
  const { theme, setTheme, toggleTheme } = useTheme();

  if (variant === "icon") {
    const next = theme === "dark" ? "light" : "dark";
    return (
      <button
        onClick={toggleTheme}
        title={`Switch to ${next} mode`}
        aria-label={`Switch to ${next} mode`}
        className={`flex items-center justify-center w-8 h-8 rounded-lg text-brand-muted hover:text-brand-linen hover:bg-brand-linen/[0.06] transition-colors ${className}`}
      >
        {theme === "dark" ? (
          // Sun — what you get if you press it.
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
          </svg>
        )}
      </button>
    );
  }

  return (
    <div
      className={`inline-flex rounded-lg border border-panel-border p-0.5 ${className}`}
      role="group"
      aria-label="Colour theme"
    >
      {(["dark", "light"] as const).map((t) => (
        <button
          key={t}
          onClick={() => setTheme(t)}
          aria-pressed={theme === t}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded-md capitalize transition-colors ${
            theme === t ? "hud-active" : "text-brand-muted hover:text-brand-linen"
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}
